import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { RoutingPath } from 'mbs-ui-shared@constants';
import { ErrorCode } from 'mbs-ui-shared@constants/errorCodes';
import { ProblemDetailsObject } from 'mbs-ui-shared@models/ProblemDetailsObject';
import { ErrorHandlerService } from 'mbs-ui-shared@services/error-handler.service';
import { getLastUrlSegment } from 'mbs-ui-shared@utils/get-url-segment.helper';
import { MANUAL_ERROR_HANDLED, URL_ERROR_TYPE } from '../error-handler.interceptor';

const subscriptionError = 'Subscription required to activate license';
const licenseCreateError = 'License can not be create';

const excludedRequests = [
  {
    url: '/api/v1/Licenses/usage/assign',
    error: subscriptionError
  },
  {
    url: '/api/v1/Licenses/usage/set-auto-renewal',
    error: subscriptionError
  },
  {
    url: '/api/licenses/grant',
    error: 'There are no available licenses'
  },
  {
    url: '/api/licenses/grant',
    error: 'Bad request'
  },
  {
    url: '/applications/connect/is-connection-allowed',
    error: 'You have not enabled Remote Connection in General Settings'
  },
  {
    url: '/applications/connect/is-connection-allowed',
    error: 'Bad request'
  },
  {
    url: 'api/brands/licenses/',
    error: subscriptionError
  },
  {
    url: 'api/administrators/',
    error: subscriptionError
  },
  {
    url: 'api/administrators/',
    error: licenseCreateError
  },
  {
    url: 'api/brands/licenses',
    error: licenseCreateError
  },
  {
    url: 'api/backup-plans/recover-password',
    error: 'Password recovery data is not specified for current generation'
  }
];

const excludeRemoteCommandErrorCodes = [
  ErrorCode.AnErrorOccured,
  ErrorCode.HyperVNotFound,
  ErrorCode.DeepSyncIsRequired,
  ErrorCode.DeepSyncIsRequiredNew,
  ErrorCode.InvalidDecryptionPassword,
  ErrorCode.InvalidBitlockerPassword,
  ErrorCode.UnknownError
];

const excludeErrorCodes = [
  ErrorCode.CompanyHasSingleStorageAccount,
  ErrorCode.NoLicences,
  ErrorCode.SubscriptionRequired,
  ErrorCode.LicenseLimitExceeded,
  ErrorCode.ObjectNotFound,
  ErrorCode.ExternalServiceRequestError,
  ErrorCode.GroupTaskNotFound,
  ErrorCode.LicenseError,
  ErrorCode.LicenseNotSet,
  ErrorCode.EndUserNoLicense,
  ErrorCode.LicenceExpired,
  ErrorCode.EndUserLicenseExpired,
  ErrorCode.TrialExpired,
  ErrorCode.LicenseIsNotSupported,
  ErrorCode.DisableTooMuchLicenses,
  ErrorCode.NeedToRevokeGoogleToken
];

const excludedRoutes = [RoutingPath.ApGettingStart, RoutingPath.ApAppsGeneral];

@Injectable({ providedIn: 'root' })
export class ThrowErrorDirectlyHelper {
  private errorService = inject(ErrorHandlerService);

  isThrowErrorDirectly(req: HttpRequest<any>, error: HttpErrorResponse): boolean {
    const errorResponse = error.error;

    return (
      this.isExcludedRoutes() ||
      this.isExcludeErrorCodes(errorResponse) ||
      this.isExcludeErrorType(errorResponse) ||
      this.isExcludeRemoteCommandErrorCodes(errorResponse, error) ||
      this.isExcludedRequest(req, error) ||
      this.isRegularError(req, error) ||
      this.isRegularConnectError(req, error)
    );
  }

  private isExcludedRoutes(): boolean {
    return excludedRoutes.includes(<RoutingPath>location.pathname);
  }

  private isExcludeErrorCodes(error: any): boolean {
    return (error?.code && excludeErrorCodes.includes(error?.code)) || (error?.typeCode && excludeErrorCodes.includes(error?.typeCode));
  }

  private isExcludeErrorType(error: ProblemDetailsObject): boolean {
    return String(error?.type).includes(URL_ERROR_TYPE) && excludeErrorCodes.includes(+getLastUrlSegment(error?.type));
  }

  private isExcludeRemoteCommandErrorCodes(errorResponse: any, error): boolean {
    if (!this.errorService.isRemoteCommand(error)) return false;

    if (excludeRemoteCommandErrorCodes.includes(+errorResponse?.errorCode)) return true;

    if (
      (error?.error?.errorCode || error?.errorCode) === 1082 &&
      (error?.error?.errorList || error.errorList || [])?.some((el) => el.id === 1546)
    ) {
      return true;
    }

    return false;
  }

  private isExcludedRequest(req: HttpRequest<any>, error: HttpErrorResponse): boolean {
    return !!excludedRequests.find(
      (excludeReq) => error?.error && req.url.includes(excludeReq.url) && this.isErrorIncludedText(error, excludeReq.error)
    );
  }

  public isLicensesError(error: HttpErrorResponse) {
    return this.isErrorIncludedText(error, subscriptionError) || this.isErrorIncludedText(error, licenseCreateError);
  }

  public isErrorIncludedText(error: HttpErrorResponse, errorText: string): boolean {
    return error.error?.title?.includes(errorText) || error.error?.detail?.includes(errorText);
  }

  private isRegularError(req: HttpRequest<any>, error: HttpErrorResponse): boolean {
    return error.status === 401 || error.status === 420 || req.headers.has(MANUAL_ERROR_HANDLED); // skip 401 or rethrow for prevent show toast
  }

  private isRegularConnectError(req: HttpRequest<any>, error: HttpErrorResponse): boolean {
    return error.status === 406 && req.url.includes('connect/is-connection-allowed'); // skip connect error to prevent toast
  }
}
